import type {
  AlertDialogBodyProps,
  AlertDialogContainerProps,
  AlertDialogDialogProps,
  AlertDialogFooterProps,
  AlertDialogHeaderProps,
} from "@project/design-system-web";
import type {ComponentProps} from "react";

import {AlertDialog, cn, useMediaQuery} from "@project/design-system-web";

import {useOverlayPortalContainer} from "../overlay-portal-context";

function AppAlertDialogContainer({className, placement, ...props}: AlertDialogContainerProps) {
  const isDesktop = useMediaQuery("(min-width: 640px)");
  const resolvedPlacement = placement ?? (isDesktop ? "center" : "top");

  return (
    <AlertDialog.Container
      className={cn("p-1! sm:p-10!", className)}
      placement={resolvedPlacement}
      {...props}
    />
  );
}

function AppAlertDialogDialog({className, ...props}: AlertDialogDialogProps) {
  return (
    <AlertDialog.Dialog
      className={cn(
        "flex max-h-[calc(100%-0.25rem)] w-[calc(100%-0.25rem)] flex-col sm:max-w-100",
        className,
      )}
      {...props}
    />
  );
}

function AppAlertDialogHeader({className, ...props}: AlertDialogHeaderProps) {
  return <AlertDialog.Header className={cn("shrink-0", className)} {...props} />;
}

function AppAlertDialogBody({className, ...props}: AlertDialogBodyProps) {
  return (
    <AlertDialog.Body
      className={cn("min-h-0 flex-1 overflow-y-auto p-4 sm:p-6", className)}
      {...props}
    />
  );
}

function AppAlertDialogFooter({className, ...props}: AlertDialogFooterProps) {
  return <AlertDialog.Footer className={cn("shrink-0", className)} {...props} />;
}

function AppAlertDialogBackdrop({
  UNSTABLE_portalContainer,
  ...props
}: ComponentProps<typeof AlertDialog.Backdrop>) {
  const scopedPortalContainer = useOverlayPortalContainer();

  // HeroUI overlay portal must be explicit here. React Aria context can split across package instances.
  return (
    <AlertDialog.Backdrop
      UNSTABLE_portalContainer={UNSTABLE_portalContainer ?? scopedPortalContainer ?? undefined}
      {...props}
    />
  );
}

export const AppAlertDialog = {
  Root: AlertDialog,
  Backdrop: AppAlertDialogBackdrop,
  Body: AppAlertDialogBody,
  CloseTrigger: AlertDialog.CloseTrigger,
  Container: AppAlertDialogContainer,
  Dialog: AppAlertDialogDialog,
  Footer: AppAlertDialogFooter,
  Header: AppAlertDialogHeader,
  Heading: AlertDialog.Heading,
  Icon: AlertDialog.Icon,
  Trigger: AlertDialog.Trigger,
};
